import React from 'react';
import { format, parseISO } from 'date-fns';
import { Table } from 'ui/Table';
import { ITableColumn, ITableRow } from 'ui/Table/types';
import { IPurchaseOrderSearchLineItem } from 'services/BookingManagerApi/types/PurchaseOrdersSearchResponse';
import { IHeadlineLineItemBreakdownExchangeRate } from 'services/BookingManagerApi/types/HeadlineLineItemBreakdown';
import { ESortOrder } from 'store/common/types';

interface DueDate {
  date: string;
  amountCents: number;
}

const formatDate = (dateString: string): string => format(parseISO(dateString), 'dd MMM yy');
const formatAmount = (amount: number): string =>
  new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

const SERVICE_TYPE_LABELS: Record<string, string> = {
  Accommodations: 'Accommodation',
  Transfers: 'Transfers',
  'Ground Services': 'Ground Service',
  Experiences: 'Activity',
  'Additional Charges': 'Additional Charges',
};

const tableColumns: ITableColumn[] = [
  { id: 'serviceDate', label: 'Service Date', width: '150px' },
  { id: 'serviceType', label: 'Service Type', width: '160px' },
  { id: 'description', label: 'Description', width: '1fr' },
  { id: 'bookingCost', label: 'Booking Cost', width: '150px', className: 'text-right' },
  { id: 'sourceCost', label: 'Source Cost', width: '150px', className: 'text-right' },
];

interface Props {
  items: IPurchaseOrderSearchLineItem[];
  currencySymbol: string;
  titleMap: Map<string, string>;
  getSourceCost: (costCents: number) => string;
  totalBookingCents: number;
  totalSourceDisplay: string;
  dueDates: DueDate[];
  exchangeRate: IHeadlineLineItemBreakdownExchangeRate | undefined;
}

export const PurchaseOrderItemsTable = ({
  items,
  currencySymbol,
  titleMap,
  getSourceCost,
  totalBookingCents,
  totalSourceDisplay,
  dueDates,
  exchangeRate,
}: Props) => {
  const tableRows: ITableRow[] = items.map(item => ({
    id: item.uuid,
    cells: [
      {
        id: 'serviceDate',
        value: item.serviceDate ? formatDate(item.serviceDate) : <span className="text-gray-60">—</span>,
      },
      {
        id: 'serviceType',
        value: (
          <span className="inline-block px-2 py-0.5 bg-gray-10 text-black rounded text-[12px]">
            {SERVICE_TYPE_LABELS[item.serviceType] ?? item.serviceType}
          </span>
        ),
      },
      {
        id: 'description',
        value: titleMap.get(item.uuid) ?? <span className="text-gray-60">—</span>,
      },
      {
        id: 'bookingCost',
        value: `${currencySymbol} ${formatAmount(item.costCents / 100)}`,
        className: 'text-right bg-green-40',
      },
      {
        id: 'sourceCost',
        value: getSourceCost(item.costCents),
        className: 'text-right bg-green-40',
      },
    ],
  }));

  const totalRow: ITableRow = {
    id: 'total',
    className: 'font-semibold',
    cells: [
      { id: 'serviceDate', value: 'TOTAL', className: 'text-gray-100' },
      { id: 'serviceType', value: null },
      { id: 'description', value: null },
      {
        id: 'bookingCost',
        value: `${currencySymbol} ${formatAmount(totalBookingCents / 100)}`,
        className: 'text-right font-semibold',
      },
      {
        id: 'sourceCost',
        value: totalSourceDisplay,
        className: 'text-right font-semibold',
      },
    ],
  };

  const dueDateRows: ITableRow[] = dueDates.map((dueDate, i) => {
    const bookingDueAmount = dueDate.amountCents / 100;
    const sourceDueAmount = !exchangeRate?.rate
      ? bookingDueAmount
      : dueDate.amountCents / 100 / exchangeRate.rate;
    const sourceCurrency = exchangeRate?.rate ? String(exchangeRate.currency) : currencySymbol;

    return {
      id: `due-date-${i}`,
      className: '!border-b-0 !border-t-0 !h-[30px] pb-[14px]',
      cells: [
        {
          id: 'serviceDate',
          value: `Due Date — ${formatDate(dueDate.date)}`,
          className: 'text-[11px] text-gray-100 uppercase tracking-wider',
          contentClassName: 'col-span-3',
        },
        { id: 'serviceType', value: null },
        { id: 'description', value: null },
        {
          id: 'bookingCost',
          value: `${currencySymbol} ${formatAmount(bookingDueAmount)}`,
          className: 'text-right',
        },
        {
          id: 'sourceCost',
          value: `${sourceCurrency} ${formatAmount(sourceDueAmount)}`,
          className: 'text-right',
        },
      ],
    };
  });

  return (
    <Table
      columns={tableColumns}
      rows={[...tableRows, totalRow, ...dueDateRows]}
      sortBy={'serviceDate'}
      sortOrder={ESortOrder.ASC}
      onSort={() => {}}
      messageWhenEmpty="No items found"
      evenColor="white"
      className="mt-[15px]"
    />
  );
};
